// TripTemplatePicker.jsx
// Lets an operator prefill a client's trip from an already-created trip package
// (the standalone `crm_trips` collection) instead of retyping the same details
// for every client on the same trip.
//
// Only the shared, descriptive fields are copied — destinations, trip type,
// progress status, start/end dates. Payment, refund and per-service bookings are
// always client-specific and are never touched. Everything stays editable after
// applying, and applying is optional.
//
// Expands inline (no absolute positioning) so it can't be clipped by the
// wizard's overflow-y-auto scroll container.
import React, { useState, useEffect, useRef } from "react";
import { api } from "../api";
import { Ico } from "./primitives";
import { G, GL, GR, tripTemplateToTripData } from "./constants";

const fmtDay = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function TripTemplatePicker({ onApply }) {
  const [open, setOpen] = useState(false);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState(null);
  const ref = useRef(null);

  // Load trip packages once, on first open.
  useEffect(() => {
    if (!open || loaded) return;
    setLoading(true);
    setError(null);
    api
      .get("/crm/trips?page=1&limit=100")
      .then((res) => {
        setTrips(res?.data || (Array.isArray(res) ? res : []));
        setLoaded(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, loaded]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const list = !q
    ? trips
    : trips.filter(
        (t) =>
          t.tripName?.toLowerCase().includes(q) ||
          (t.destinations || []).some((d) => d.toLowerCase().includes(q)) ||
          t.tripType?.toLowerCase().includes(q)
      );

  const pick = (t) => {
    onApply(tripTemplateToTripData(t));
    setApplied(t.tripName);
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      className="rounded-xl border border-dashed p-3"
      style={{ borderColor: GR, background: GL }}
      ref={ref}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: G }}>
            Copy from an existing trip
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">
            {applied
              ? `Prefilled from “${applied}” — edit anything below.`
              : "Optional — prefills destinations, type, status and dates."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-black whitespace-nowrap bg-white border transition-colors hover:shadow-sm"
          style={{ color: G, borderColor: GR }}
        >
          <Ico d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" sz={11} col={G} />
          {open ? "Close" : applied ? "Change" : "Select Trip"}
        </button>
      </div>

      {open && (
        <div className="mt-3 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search trips by name or destination…"
                className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] bg-white"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-[rgb(var(--brand-rgb))] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {error && <p className="text-center py-5 text-xs text-rose-400">{error}</p>}
            {!loading && !error && !list.length && (
              <p className="text-center py-5 text-xs text-slate-400">
                {trips.length ? "No trips match your search" : "No trip packages created yet"}
              </p>
            )}
            {!loading &&
              list.map((t) => (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => pick(t)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-700 truncate">{t.tripName}</p>
                    <p className="text-[11px] text-slate-400 truncate">
                      {(t.destinations || []).join(", ") || "No destinations"}
                      {t.startDate && ` · ${fmtDay(t.startDate)}`}
                    </p>
                  </div>
                  <span
                    className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: GL, color: G }}
                  >
                    {t.tripType || "other"}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}